import { Box, IconButton, Typography } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CloseIcon from "@mui/icons-material/Close";
import { useLocation } from "react-router-dom";
import type { ThreadHeaderProps } from "stream-chat-react";
import { useChannelStateContext } from "stream-chat-react";
import { Colors } from "Components/Layout/Themes/Colors";
import { useChatChannelDisplayInfo } from "Hooks/Chat/useChatChannelDisplayInfo";
import { getSearchWithThreadParam } from "Hooks/Chat/useChatThreadUrlSync";
import { useIsMobile } from "Hooks/useIsMobile";
import { useNavigate } from "Navigation/useNavigate";
import { Resources, useResource } from "Translations/Resources";

/**
 * Header of the thread panel/page, replacing Stream's default. Closing does NOT call Stream's
 * `closeThread` directly — it drops the `?thread` param from the URL, and useChatThreadUrlSync
 * closes the thread when the param disappears. That keeps the in-app button, browser back and
 * the Android hardware back on one code path.
 *
 * When the thread entry was pushed from this conversation (the previous entry in the app's
 * history stack has the same pathname) closing POPS it, so the browser history looks as if the
 * thread was never opened and the next hardware back leaves the conversation rather than
 * reopening the thread. A deep-linked thread has no such entry, so it is replaced in place.
 *
 * On mobile the thread is a page on the stack, so the affordance is a back chevron; on desktop it
 * sits beside the conversation and gets a close X. The bar is a deeper blue than the channel
 * header so the two read as distinct, and the darker shade says "one level down".
 */
export const ChatThreadHeader = ({ thread }: ThreadHeaderProps) => {
  const { t } = useResource();
  // A thread under a multi-payer request lists its payments, so it is titled as such; any other
  // thread (none today, see IS_MANUAL_THREADING_ENABLED) keeps the generic title.
  const isPaymentThread = (thread.attachments ?? []).some(
    (a) => a.type === "payment_request_multi",
  );
  const title = t(
    isPaymentThread
      ? Resources.Chat.MultiPayerRequest.ThreadTitle
      : Resources.Chat.Thread.Title,
  );
  const isMobile = useIsMobile();
  const { channel } = useChannelStateContext();
  const { displayName } = useChatChannelDisplayInfo(
    channel,
    channel.state.membership?.user?.id,
  );

  const location = useLocation();
  const navigate = useNavigate();

  const previousLocation = location.state?.locationHistoryStack?.[0];
  const isThreadPushedFromConversation =
    previousLocation?.pathname === location.pathname;

  const handleClose = () => {
    if (isThreadPushedFromConversation) {
      window.history.back();
      return;
    }
    navigate(
      {
        pathname: location.pathname,
        search: getSearchWithThreadParam(location.search, null),
      },
      { replace: true },
    );
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      bgcolor={Colors.PrimaryDeep}
      color="common.white"
      p={2.5}
      pl={isMobile ? 0 : undefined}
      gap={1}
      flexShrink={0}
      minWidth={0}
    >
      {isMobile && (
        <IconButton
          onClick={handleClose}
          sx={{ color: "common.white" }}
          aria-label={t(Resources.Common.Back)}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}
      {/* Pinned to the channel header's 40px avatar so the two bars share a bottom edge. */}
      <Box
        flex={1}
        minWidth={0}
        height={40}
        display="flex"
        flexDirection="column"
        justifyContent="center"
      >
        <Typography fontWeight={700} lineHeight="22px" noWrap>
          {title}
        </Typography>
        <Typography
          fontSize={13}
          lineHeight="18px"
          color="rgba(255, 255, 255, 0.7)"
          noWrap
        >
          {displayName}
        </Typography>
      </Box>
      {!isMobile && (
        <IconButton
          onClick={handleClose}
          sx={{ color: "common.white" }}
          aria-label={t(Resources.Common.Close)}
        >
          <CloseIcon />
        </IconButton>
      )}
    </Box>
  );
};
